/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type NotificationType } from '@az/starc-ui/dist/components/notification/Notification.types';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useState,
  type ReactNode,
  useRef,
  type RefObject,
  useEffect,
  use,
} from 'react';

export type Flows =
  | 'login'
  | 'chooseSignInMethod'
  | 'OTPFlow'
  | 'password'
  | 'forgotPassword'
  | 'resetPassword'
  | 'resetPasswordEmailSent'
  | 'autoCreatedOnlineAccount'
  | 'manualCreateAccount'
  | 'rewardsEnterId'
  | 'signInOrCreateNewPW';

export type RewardsState = 'initial' | 'loggedIn' | 'enterId';

export type ForgotPasswordState = 'initial' | 'requested';

type SignInFlowTypeProviderType = {
  setFlowTypeFn: (flowTypeValue: Flows) => void;
  setRewardsStateFn: (rewardsStateValue: RewardsState) => void;
  setUserLoginFn: (value: string) => void;
  setShowGlobalNotificationFn: (value: boolean) => void;
  resetSignInFlowTypeContextFn: () => void;
  setIsSignInModalOpened: (value: boolean) => void;
  notificationText: string;
  notificationTitle: string;
  notificationType: NotificationType;
  isSignInModalOpened: boolean;
  userLogin: string;
  flowType: Flows;
  rewardsState: string;
  showGlobalNotification: boolean;
  previousFlowType: string;
  setNotificationText: Dispatch<SetStateAction<string>>;
  setNotificationTitle: Dispatch<SetStateAction<string>>;
  setNotificationType: Dispatch<SetStateAction<NotificationType>>;
  setGlobalNotification: (notification: GlobalNotification) => void;
  onCloseNotification: VoidFunction;
  isOtpOnlyFlow: boolean;
  setIsOtpOnlyFlow: Dispatch<SetStateAction<boolean>>;
  pageCountRef: RefObject<number>;
};

export const SignInFlowTypeContext = createContext<SignInFlowTypeProviderType | null>(null);

type GlobalNotification = {
  title: string;
  text: string;
  type: NotificationType;
};

type SignInFlowProviderProps = {
  children: ReactNode;
};

export const SignInFlowTypeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}: SignInFlowProviderProps) => {
  const router = useLegacyRouter();
  const [flowType, setFlowType] = useState<Flows>('login');
  const [rewardsState, setRewardsState] = useState<RewardsState>('initial');
  const [previousFlowType, setPreviousFlowType] = useState('');
  const [userLogin, setUserLogin] = useState('');
  const [showGlobalNotification, setShowGlobalNotification] = useState<boolean>(false);
  const [isSignInModalOpened, setIsSignInModalOpened] = useState(false);
  const [notificationText, setNotificationText] = useState('');
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationType, setNotificationType] = useState<NotificationType>('info');
  const [isOtpOnlyFlow, setIsOtpOnlyFlow] = useState<boolean>(false);
  const pageCountRef = useRef<number>(0);

  useEffect(() => {
    pageCountRef.current++;
  }, [router.asPath]);

  const setFlowTypeFn = (currentFlowTypeValue: Flows) => {
    // do not update previousFlowType if 'Resend Code' link was used
    if (
      currentFlowTypeValue === 'OTPFlow' &&
      (previousFlowType === 'forgotPassword' || previousFlowType === 'chooseSignInMethod')
    ) {
      setPreviousFlowType(previousFlowType);
    } else {
      setPreviousFlowType(flowType);
    }

    setFlowType(currentFlowTypeValue);
  };

  const setRewardsStateFn = (rewardsStateValue: RewardsState) => setRewardsState(rewardsStateValue);

  const setUserLoginFn = (value: string) => setUserLogin(value);

  const setShowGlobalNotificationFn = (value: boolean) => setShowGlobalNotification(value);

  const setGlobalNotification = ({ text, title, type }: GlobalNotification) => {
    setNotificationTitle(title);
    setNotificationText(text);
    setNotificationType(type);
  };

  const onCloseNotification = () => {
    setShowGlobalNotification(false);
    setGlobalNotification({ text: '', title: '', type: 'info' });
  };

  const resetSignInFlowTypeContextFn = () => {
    setFlowTypeFn('login');
    setUserLoginFn('');
  };

  return (
    <SignInFlowTypeContext
      value={{
        setFlowTypeFn,
        setRewardsStateFn,
        setUserLoginFn,
        setShowGlobalNotificationFn,
        resetSignInFlowTypeContextFn,
        setIsSignInModalOpened,
        setNotificationText,
        setNotificationTitle,
        setNotificationType,
        setGlobalNotification,
        onCloseNotification,
        notificationText,
        notificationTitle,
        notificationType,
        isSignInModalOpened,
        flowType: flowType,
        rewardsState: rewardsState,
        userLogin: userLogin,
        showGlobalNotification: showGlobalNotification,
        previousFlowType: previousFlowType,
        isOtpOnlyFlow,
        setIsOtpOnlyFlow,
        pageCountRef,
      }}
    >
      {children}
    </SignInFlowTypeContext>
  );
};

export const useSignInFlowType = () => {
  const context = use(SignInFlowTypeContext);
  if (!context) {
    throw new Error('useSignInFlowType must be used within the SignInFlowTypeProvider');
  }
  return context;
};
